/**
 * What an appointment is *for*: the seven agendas a practice books one under.
 *
 * A closed list rather than a box to type in, because the agenda is read far
 * more often than it is written — it is a column of the client's history, a
 * line on every row of My task, and a row of the Meeting dialog — and a column
 * of free text is one where "Follow up", "follow-up" and "Followup" are three
 * different appointments to everything that ever tries to count them.
 *
 * The codes are the backend's own `TextChoices`; the labels are how the design
 * writes them. One list in one place, the way `MEETING_APPS` is: the dropdown
 * on both booking dialogs and the reader below are the only two things that
 * know the pairing, and two copies would drift the moment an eighth is added.
 */

import type { DropdownOption } from "../../components/ui";

/** The agenda as the backend spells it. `FOLLOW_UP`, not `Follow-up`. */
export type MeetingAgendaCode =
  | "CONSULTATION"
  | "INITIAL_CONSULTATION"
  | "FOLLOW_UP"
  | "FEEDBACK_CALL"
  | "DIET_PLAN_REVIEW"
  | "PROGRESS_CHECK"
  | "OTHER";

/**
 * The seven, in the order the dropdown draws them.
 *
 * Not alphabetical: it runs roughly in the order a client meets them — the
 * first consultation, the follow-ups and reviews that come out of it, then the
 * catch-all. A list somebody reads top to bottom once and then picks from by
 * position is worth more than one sorted by a rule nobody is applying.
 */
export const MEETING_AGENDAS: { code: MeetingAgendaCode; label: string }[] = [
  { code: "CONSULTATION", label: "Consultation" },
  { code: "INITIAL_CONSULTATION", label: "Initial consultation" },
  { code: "FOLLOW_UP", label: "Follow-up" },
  { code: "FEEDBACK_CALL", label: "Feedback call" },
  { code: "DIET_PLAN_REVIEW", label: "Diet plan review" },
  { code: "PROGRESS_CHECK", label: "Progress check" },
  { code: "OTHER", label: "Other" },
];

/** The same list as the dropdown takes it. */
export const AGENDA_OPTIONS: DropdownOption[] = MEETING_AGENDAS.map(
  ({ code, label }) => ({ value: code, label })
);

/**
 * One agenda as it should be read out loud, out of whatever the record
 * carries.
 *
 * Three answers rather than one, and the middle one is why this is a function:
 *
 *   `FOLLOW_UP`   a code this app knows        → "Follow-up"
 *   `Follow-up`   a label, matched case-blind  → "Follow-up"
 *   anything else a string nothing recognises  → itself, unchanged
 *
 * The second case is not hypothetical. `meeting_agenda` was a free-text field
 * before it was a choice, and the records already written under it carry the
 * label rather than the code — so a strict reader would blank out every
 * appointment booked before this screen existed. The third case is the same
 * courtesy extended forwards: an eighth agenda added at the backend shows as
 * whatever the backend called it rather than as an empty cell, until this list
 * learns about it.
 *
 * `null` where there is none, which is an ordinary answer — the dialog does
 * not insist on one.
 */
export const agendaLabel = (agenda: string | null | undefined): string | null => {
  const value = (agenda ?? "").trim();
  if (!value) return null;

  const match = MEETING_AGENDAS.find(
    (entry) =>
      entry.code === value ||
      entry.label.toLowerCase() === value.toLowerCase()
  );

  return match ? match.label : value;
};

/**
 * The dropdown's value for an agenda already on a record — the reverse of the
 * above, and only for the one caller that needs it: a dialog opened on an
 * appointment that already has one.
 *
 * A label is translated back to its code, so a record written before the
 * choices existed is re-sent as the code rather than re-sent as the prose it
 * was stored as. Anything unrecognised comes back `""`, which the dropdown
 * reads as unanswered: offering a value the list does not contain would draw a
 * field that looks empty and is not.
 */
export const agendaCodeOf = (
  agenda: string | null | undefined
): MeetingAgendaCode | "" => {
  const value = (agenda ?? "").trim();
  if (!value) return "";

  const match = MEETING_AGENDAS.find(
    (entry) =>
      entry.code === value ||
      entry.label.toLowerCase() === value.toLowerCase()
  );

  return match ? match.code : "";
};

/**
 * How long a description may run.
 *
 * Five hundred, which is a long paragraph — room for what the session is about
 * and what to have to hand, which is what the box is for, and not room for the
 * session notes themselves. Enforced on the control as a `maxLength` and said
 * out loud as a counter beside it, so the limit is met by the field refusing
 * the 501st character rather than by the server refusing the booking.
 */
export const MAX_DESCRIPTION = 500;
